import { useState } from "react";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";

export default function ContactUs() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: ""
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);
    // Simulate API call
    setTimeout(() => {
      toast.success("Thank you! We'll get back to you within 24 hours.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setLoading(false);
    }, 1000);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      details: "support@biznexus.com",
      subtext: "We'll respond within 24 hours"
    },
    {
      icon: Phone,
      title: "Phone",
      details: "+91 80077 73707",
      subtext: "Mon-Sat, 9 AM - 6 PM IST"
    },
    {
      icon: MapPin,
      title: "Office",
      details: "Mumbai, Maharashtra",
      subtext: "India"
    },
    {
      icon: Clock,
      title: "Business Hours",
      details: "Monday - Saturday",
      subtext: "9:00 AM - 6:00 PM IST"
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Header */}
        <div className="text-center mb-16" data-testid="contact-header">
          <h1 className="text-5xl md:text-6xl font-bold text-slate-900 mb-6">
            Get In Touch
          </h1>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto">
            Have questions? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
          </p>
        </div>

        {/* Contact Info Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {contactInfo.map((info, index) => {
            const Icon = info.icon;
            return (
              <Card key={index} className="bg-white border border-slate-100 hover:shadow-lg transition-all">
                <CardContent className="p-6 text-center">
                  <Icon className="w-10 h-10 text-blue-600 mx-auto mb-4" />
                  <h3 className="font-semibold text-slate-900 mb-2">{info.title}</h3>
                  <p className="text-slate-900 font-medium mb-1">{info.details}</p>
                  <p className="text-sm text-slate-500">{info.subtext}</p>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Contact Form & Map */}
        <div className="grid lg:grid-cols-2 gap-12">
          {/* Form */}
          <Card className="bg-white border border-slate-100">
            <CardContent className="p-8">
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Send Us a Message</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    required
                    data-testid="contact-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                    data-testid="contact-email-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number</Label>
                  <Input
                    id="phone"
                    type="tel"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    data-testid="contact-phone-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    value={form.subject}
                    onChange={(e) => setForm({ ...form, subject: e.target.value })}
                    required
                    data-testid="contact-subject-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    rows={6}
                    value={form.message}
                    onChange={(e) => setForm({ ...form, message: e.target.value })}
                    required
                    data-testid="contact-message-input"
                  />
                </div>
                <Button
                  type="submit"
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  disabled={loading}
                  data-testid="contact-submit-button"
                >
                  {loading ? "Sending..." : (
                    <>
                      <Send className="w-4 h-4 mr-2" />
                      Send Message
                    </>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>

          {/* Info Section */}
          <div className="space-y-6">
            <Card className="bg-gradient-to-br from-blue-600 to-indigo-600 text-white border-0">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold mb-4">Why Contact Us?</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <span className="text-blue-200 mr-2">✓</span>
                    <span>Get expert guidance on buying or selling businesses</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-200 mr-2">✓</span>
                    <span>Learn about our verification process</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-200 mr-2">✓</span>
                    <span>Connect with our consultant network</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-200 mr-2">✓</span>
                    <span>Explore partnership opportunities</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-blue-200 mr-2">✓</span>
                    <span>Report issues or provide feedback</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="bg-white border border-slate-100">
              <CardContent className="p-8">
                <h3 className="text-2xl font-bold text-slate-900 mb-4">Frequently Asked Questions</h3>
                <p className="text-slate-600 mb-4">
                  Before reaching out, check our FAQ section for quick answers to common questions.
                </p>
                <a href="/policies" className="text-blue-600 hover:text-blue-700 font-medium">
                  View Business Policies →
                </a>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}